import { useEffect, useRef, useState } from "react";

/**
 * Service-worker registration and the automatic safe-moment update.
 *
 * A DEPLOY REACHING THE USER IS AN APP-WIDE CONCERN, not a game one.
 * This lived inside the install-prompt hook, which is mounted only by
 * the game screen — so a reader sitting in the lobby had nothing
 * polling for a new build and nothing to activate one that was
 * already waiting. They stayed on whatever build they last happened
 * to load while inside a hand.
 *
 * What it does:
 *   1. Registers the Workbox-generated SW on mount (production only —
 *      `devOptions.enabled: false` means dev ships no SW).
 *   2. Adopts a worker that is ALREADY waiting. Workbox reports
 *      transitions it witnesses, and a build parked on a previous load
 *      already made its transition; without this check the ordinary
 *      post-deploy case — reload, park, reload again — asked nobody to
 *      activate it, and the installation stayed pinned to the first
 *      build it ever cached.
 *   3. Polls `registration.update()` on a timer and on tab focus /
 *      visibility-restore, so a deploy lands while the app stays open.
 *   4. Activates the waiting worker at the next SAFE moment — no
 *      prompt, no tap — and reloads onto the fresh shell exactly once.
 *
 * "Safe" is the caller's word. Today it means "not at a table": a
 * reload survives the session but not the table, and the reader would
 * land back in the lobby with a seat to re-claim by hand — which can
 * cost them the seat, since three missed turns bench a player.
 */

/** Periodic service-worker update poll interval. 20 min is a
 *  deliberate middle of the investigation's 15-30 min band: frequent
 *  enough that an open tab picks up a deploy within a single coffee
 *  break, infrequent enough that the `/sw.js` re-fetch is negligible
 *  network. A deploy is ALSO picked up immediately on tab focus /
 *  visibility-restore, so this timer is the backstop for a tab that
 *  stays foregrounded for a long stretch. */
export const SW_UPDATE_POLL_MS = 20 * 60 * 1_000; // 20 minutes

/**
 * Module-level one-shot reload guard. The SW `controlling` event can
 * fire more than once across a session (e.g. a second deploy lands
 * while the user is still reading); the page reload tears the tab
 * down so the FIRST `controlling` is the only one that should reload.
 * Guarding at module scope (not per-hook-instance) means a re-mount
 * of the hook cannot reset it and re-arm a reload loop.
 */
let reloadArmed = false;

/**
 * @param safeToReload — when a new SW is waiting AND this is `true`,
 *   the hook activates it (which reloads the page). Re-evaluated
 *   whenever the flag flips, so a build that arrived while the reader
 *   was at a table activates the moment they leave it, with no further
 *   user action.
 */
export function useServiceWorkerUpdate(safeToReload = true): void {
  // True once a fresh build has precached + parked in the SW
  // `waiting` state. Drives the safe-moment activation effect below.
  const [updateWaiting, setUpdateWaiting] = useState(false);
  const workboxRef = useRef<{
    addEventListener: (type: string, listener: (e: Event) => void) => void;
    messageSkipWaiting: () => void;
    register: () => Promise<ServiceWorkerRegistration | undefined>;
  } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;
    if (import.meta.env.DEV) return; // dev: no SW per VitePWA devOptions
    let cancelled = false;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let registration: ServiceWorkerRegistration | null = null;

    // Re-fetch `/sw.js`; the SW lifecycle fires `waiting` if the
    // server is serving a newer build. Rejection (offline / no
    // network) is swallowed — there is simply nothing to update.
    const pollUpdate = (): void => {
      if (registration === null) return;
      void registration.update().catch(() => {
        // offline / transient — ignore; the next poll retries.
      });
    };
    const onVisible = (): void => {
      if (document.visibilityState === "visible") pollUpdate();
    };

    void (async () => {
      try {
        const { Workbox } = await import("workbox-window");
        if (cancelled) return;
        const wb = new Workbox("/sw.js", { scope: "/" });
        workboxRef.current = wb as unknown as typeof workboxRef.current;
        wb.addEventListener("waiting", () => {
          // New build precached, parked in `waiting`. Flag it; the
          // safe-moment effect below decides WHEN to activate.
          setUpdateWaiting(true);
        });
        wb.addEventListener("controlling", (event) => {
          // A FIRST install also takes control, and reloading the page
          // under a reader who just arrived would be a shove for no
          // reason — there is no newer build to show them, they are
          // already on it. `isUpdate` is workbox's answer to "was
          // something else driving this page before me": only then is
          // there an old shell on screen worth replacing.
          const isUpdate = (event as { isUpdate?: boolean }).isUpdate === true;
          if (!isUpdate) return;
          // The waiting worker took control — reload onto the new
          // shell. One-shot guard prevents a second `controlling`
          // (a deploy landing during the read window) from racing a
          // reload loop; the page teardown also makes this moot, but
          // the guard is belt-and-braces.
          if (reloadArmed) return;
          reloadArmed = true;
          window.location.reload();
        });
        const reg = await wb.register();
        if (cancelled) return;
        registration = reg ?? null;
        // A worker that reached `waiting` on a PREVIOUS load never
        // fires the event above — workbox reports transitions it
        // witnesses, and this one already happened.
        //
        // That is the ordinary case after a deploy. The first reload
        // fetches the new worker and parks it; every reload after that
        // finds it already parked, sees no event, and asks nobody to
        // activate it. The installation stays pinned to the first build
        // it ever cached — a new deploy could not reach the user at
        // all, and a hard reload did not change that, because the
        // worker serving the old assets was never told to step aside.
        if (registration?.waiting != null) {
          setUpdateWaiting(true);
        }
        // Arm the periodic poll + the return-to-tab poll so a deploy
        // is picked up without a manual refresh.
        pollTimer = setInterval(pollUpdate, SW_UPDATE_POLL_MS);
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", pollUpdate);
      } catch (err) {
        // Registration failure is non-fatal — the app still runs
        // as a normal SPA. Surface for diagnostics (the lint config
        // permits `console.warn` / `console.error`).
        console.warn("[pwa] SW registration failed", err);
      }
    })();
    return () => {
      cancelled = true;
      if (pollTimer !== null) clearInterval(pollTimer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", pollUpdate);
    };
  }, []);

  // SAFE-MOMENT ACTIVATION. When a fresh build is waiting AND the
  // caller says it's safe to reload, tell the waiting SW to skip
  // waiting. The `controlling` listener above then reloads the page
  // onto the new shell. Re-runs whenever `safeToReload` flips, so a
  // build that arrived mid-hand activates the instant the hand ends —
  // no prompt, no tap. If it is NOT safe yet, the effect simply waits
  // for the next `safeToReload === true` render.
  useEffect(() => {
    if (!updateWaiting) return;
    if (!safeToReload) return;
    const wb = workboxRef.current;
    if (wb === null) return;
    wb.messageSkipWaiting();
  }, [updateWaiting, safeToReload]);
}
